/**
 * Bundle Pack Presets for Featured Product Section
 * Version: 1.0.0
 * Standalone JavaScript - Does NOT depend on BundlePackAddon
 *
 * Manages preset pills, product card quantity controls,
 * quantity meter, price updates, and cart interception.
 */

(function() {
  'use strict';

  class BundlePackPresets {
    constructor(container) {
      this.container = container;
      this.cards = container.querySelectorAll('.bundle-pack-card');
      this.quantityInputs = container.querySelectorAll('.bundle-pack-card__quantity-input');
      this.quantityMeter = container.querySelector('[data-quantity-meter-addon]');

      this.selectedVariants = new Map();

      // Trouver le formulaire produit principal (pas dans un drawer)
      let formCandidate = container.closest('form[action*="/cart/add"]');

      if (!formCandidate) {
        formCandidate = container.parentElement?.querySelector('form[action*="/cart/add"]');
      }

      if (!formCandidate) {
        const allForms = document.querySelectorAll('form[action*="/cart/add"]');
        for (const form of allForms) {
          const isInDrawer = form.closest('cart-drawer') ||
                             form.closest('#CartDrawer') ||
                             form.classList.contains('upsell-drawer-form');
          if (!isInDrawer) {
            formCandidate = form;
            break;
          }
        }
      }

      if (formCandidate && (
          formCandidate.closest('cart-drawer') ||
          formCandidate.closest('#CartDrawer') ||
          formCandidate.classList.contains('upsell-drawer-form')
      )) {
        this.productForm = null;
      } else {
        this.productForm = formCandidate;
      }

      // Configuration par défaut des paliers
      this.tiers = [
        { qty: 1, text: '1', price: '' },
        { qty: 2, text: '2', price: '' },
        { qty: 3, text: '3', price: '' },
        { qty: 4, text: '4', price: '' }
      ];

      this.discounts = {
        '1': { type: 'none', value: 0 },
        '2': { type: 'none', value: 0 },
        '3': { type: 'none', value: 0 },
        '4': { type: 'none', value: 0 },
        '5': { type: 'none', value: 0 }
      };

      this.presets = [];
      this.activePresetIndex = 'free';
      this.freePackLabel = 'Mon pack libre';

      this.init();
    }

    init() {
      try {
        this.loadConfiguration();
        this.clearLocalStorage();
        this.bindPresetPills();
        this.bindQuantityControls();
        this.initializeQuantityButtons();
        this.updateQuantityMeter();
        this.updateButtonPrice();
        this.captureInitialSubmitState();
        this.updateSubmitButtonState();
        this.interceptFetchRequests();
        this.initializeScrollIndicator();
        this.initGridLayout();

        console.log('[BundlePackPresets] Initialized', {
          cards: this.cards.length,
          presets: this.presets.length,
          pills: this.container.querySelectorAll('.bundle-presets-pill').length
        });

        // Écouter quand ButtonPriceManager est prêt
        document.addEventListener('buttonPriceManagerReady', () => {
          if (this.selectedVariants.size > 0) {
            this.updateButtonPrice();
          }
        });
      } catch (e) {
        console.error('[BundlePackPresets] Init error:', e);
      }
    }

    // ══════════════════════════════════════════
    // Configuration
    // ══════════════════════════════════════════

    loadConfiguration() {
      const config = this.container.dataset;

      if (config.tiers) {
        try {
          this.tiers = JSON.parse(config.tiers);
        } catch (e) {
          // Use defaults
        }
      }

      if (config.discounts) {
        try {
          this.discounts = JSON.parse(config.discounts);
        } catch (e) {
          // Use defaults
        }
      }

      // Presets
      if (config.presets) {
        try {
          this.presets = JSON.parse(config.presets);
        } catch (e) {
          this.presets = [];
        }
      }

      this.freePackLabel = config.freePackLabel || 'Mon pack libre';

      // Configuration de la remise globale
      this.globalDiscountEnabled = config.globalDiscountEnabled === 'true';
      this.globalDiscountPercentage = parseFloat(config.globalDiscountPercentage) || 0;
      this.globalDiscountColor = config.globalDiscountColor || '#dc2626';

      if (this.globalDiscountEnabled && this.globalDiscountPercentage > 0) {
        this.applyGlobalDiscountToPrices();
        if (this.quantityMeter) {
          this.quantityMeter.classList.add('quantity-meter-addon--global-discount');
        }
      } else {
        if (this.quantityMeter) {
          this.quantityMeter.classList.remove('quantity-meter-addon--global-discount');
        }
      }
    }

    applyGlobalDiscountToPrices() {
      const priceElements = this.container.querySelectorAll('.quantity-meter-addon-item__price[data-original-price]');

      priceElements.forEach(priceEl => {
        const originalPriceText = priceEl.dataset.originalPrice;
        const priceRegex = /(\d+(?:[.,]\d+)?)\s*([€$£])/;
        const match = originalPriceText.match(priceRegex);

        if (match) {
          const originalPrice = parseFloat(match[1].replace(',', '.'));
          const currency = match[2];
          const suffix = originalPriceText.substring(match.index + match[0].length);
          const discountedPrice = originalPrice * (1 - this.globalDiscountPercentage / 100);

          let formattedDiscountedPrice;
          if (originalPriceText.includes(',') || originalPriceText.includes('.')) {
            formattedDiscountedPrice = discountedPrice.toFixed(2).replace('.', ',');
          } else {
            formattedDiscountedPrice = Math.round(discountedPrice).toString();
          }

          priceEl.innerHTML = `
            <span class="quantity-meter-addon-item__price--original">${originalPriceText}</span>
            <span class="quantity-meter-addon-item__price--discounted" style="color: ${this.globalDiscountColor};">
              ${formattedDiscountedPrice}${currency}${suffix}
            </span>
          `;
        }
      });
    }

    // ══════════════════════════════════════════
    // Preset Pills
    // ══════════════════════════════════════════

    bindPresetPills() {
      const pills = this.container.querySelectorAll('.bundle-presets-pill[data-preset-index]');
      pills.forEach(pill => {
        pill.addEventListener('click', (e) => {
          e.preventDefault();
          const index = pill.dataset.presetIndex;
          this.activatePreset(index);
        });
      });
    }

    activatePreset(index) {
      // 1. Mettre à jour le pill actif
      const pills = this.container.querySelectorAll('.bundle-presets-pill');
      pills.forEach(pill => pill.classList.remove('bundle-presets-pill--active'));
      const targetPill = this.container.querySelector(`[data-preset-index="${index}"]`);
      if (targetPill) targetPill.classList.add('bundle-presets-pill--active');

      // 2. Tracker le preset actif
      this.activePresetIndex = index;

      // 3. Reset toutes les quantités
      this.resetAllQuantities();

      // 4. Si preset numérique, appliquer les quantités
      if (index !== 'free' && this.presets[parseInt(index)]) {
        const preset = this.presets[parseInt(index)];
        this.setCardQuantities(preset.products);
      }

      // 5. Mettre à jour tout l'état downstream
      this.updateQuantityMeter();
      this.updateButtonPrice();
      this.updateSubmitButtonState();
      this.saveToLocalStorage();
    }

    setCardQuantities(products) {
      if (!products || !products.length) return;

      products.forEach(({ variantId, qty }) => {
        const card = this.container.querySelector(`.bundle-pack-card[data-variant-id="${variantId}"]`);
        if (!card) return;

        const input = card.querySelector('.bundle-pack-card__quantity-input');
        if (!input) return;

        const quantity = parseInt(qty) || 0;
        input.value = quantity;

        if (quantity > 0) {
          const compareAtPrice = card.dataset.variantCompareAtPrice;
          this.selectedVariants.set(variantId, {
            quantity: quantity,
            price: parseInt(card.dataset.variantPrice),
            compare_at_price: compareAtPrice ? parseInt(compareAtPrice) : null,
            title: card.querySelector('.bundle-pack-card__title')?.textContent.trim() || ''
          });
          card.classList.add('bundle-pack-card--selected');
        }

        this.updateQuantityButtons(input);
      });
    }

    resetAllQuantities() {
      this.selectedVariants.clear();

      this.quantityInputs.forEach(input => {
        input.value = 0;
        this.updateQuantityButtons(input);
      });

      this.cards.forEach(card => {
        card.classList.remove('bundle-pack-card--selected');
      });
    }

    checkAndSwitchToFreePack() {
      if (this.activePresetIndex === 'free') return;

      // L'utilisateur a modifié manuellement une quantité → switch vers pack libre
      const pills = this.container.querySelectorAll('.bundle-presets-pill');
      pills.forEach(pill => pill.classList.remove('bundle-presets-pill--active'));
      const freePill = this.container.querySelector('[data-preset-index="free"]');
      if (freePill) freePill.classList.add('bundle-presets-pill--active');
      this.activePresetIndex = 'free';
    }

    // ══════════════════════════════════════════
    // Quantity Controls
    // ══════════════════════════════════════════

    bindQuantityControls() {
      this.quantityInputs.forEach(input => {
        const quantityContainer = input.closest('.bundle-pack-card__quantity-controls');
        if (!quantityContainer) return;

        const plusButton = quantityContainer.querySelector('[name="plus"]');
        const minusButton = quantityContainer.querySelector('[name="minus"]');

        if (plusButton) {
          plusButton.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.incrementQuantity(input);
          });
        }

        if (minusButton) {
          minusButton.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.decrementQuantity(input);
          });
        }
      });
    }

    incrementQuantity(input) {
      const currentValue = parseInt(input.value) || 0;
      const max = parseInt(input.dataset.max) || 999;

      if (currentValue < max) {
        const newValue = currentValue + 1;
        input.value = newValue;
        this.handleQuantityChangeDirectly(input, newValue);
      }
    }

    decrementQuantity(input) {
      const currentValue = parseInt(input.value) || 0;

      if (currentValue > 0) {
        const newValue = currentValue - 1;
        input.value = newValue;
        this.handleQuantityChangeDirectly(input, newValue);
      }
    }

    handleQuantityChangeDirectly(input, quantity) {
      const variantId = input.dataset.variantId;
      const card = input.closest('.bundle-pack-card');
      const max = parseInt(input.dataset.max) || 999;

      if (quantity < 0) {
        input.value = 0;
        quantity = 0;
      }

      if (quantity > max) {
        input.value = max;
        quantity = max;
      }

      if (quantity > 0) {
        const compareAtPrice = card.dataset.variantCompareAtPrice;
        this.selectedVariants.set(variantId, {
          quantity: quantity,
          price: parseInt(card.dataset.variantPrice),
          compare_at_price: compareAtPrice ? parseInt(compareAtPrice) : null,
          // Toutes les variantes du bundle ne portent pas forcément le plan d'abonnement.
          hasSellingPlan: card.dataset.hasSellingPlan === 'true',
          title: card.querySelector('.bundle-pack-card__title')?.textContent.trim() || ''
        });
        card.classList.add('bundle-pack-card--selected');
      } else {
        this.selectedVariants.delete(variantId);
        card.classList.remove('bundle-pack-card--selected');
      }

      this.updateQuantityButtons(input);
      this.checkAndSwitchToFreePack();
      this.updateQuantityMeter();
      this.updateButtonPrice();
      this.updateSubmitButtonState();
      this.saveToLocalStorage();
    }

    updateQuantityButtons(input) {
      const quantityContainer = input.closest('.bundle-pack-card__quantity-controls');
      if (!quantityContainer) return;

      const plusButton = quantityContainer.querySelector('[name="plus"]');
      const minusButton = quantityContainer.querySelector('[name="minus"]');

      const value = parseInt(input.value) || 0;
      const max = parseInt(input.dataset.max) || 999;

      const shouldDisableMinus = value <= 0;
      const shouldDisablePlus = value >= max;

      if (minusButton) {
        minusButton.disabled = shouldDisableMinus;
        minusButton.classList.toggle('disabled', shouldDisableMinus);
      }
      if (plusButton) {
        plusButton.disabled = shouldDisablePlus;
        plusButton.classList.toggle('disabled', shouldDisablePlus);
      }
    }

    initializeQuantityButtons() {
      this.quantityInputs.forEach(input => {
        this.updateQuantityButtons(input);
      });
    }

    // ══════════════════════════════════════════
    // Quantity Meter
    // ══════════════════════════════════════════

    updateQuantityMeter() {
      if (!this.quantityMeter) return;

      let totalQuantity = 0;
      this.selectedVariants.forEach(data => {
        totalQuantity += data.quantity;
      });

      const progressLine = this.quantityMeter.querySelector('[data-progress-line-addon]');
      const items = this.quantityMeter.querySelectorAll('.quantity-meter-addon-item');

      let currentTier = 0;
      let lastActiveTier = -1;

      for (let i = 0; i < this.tiers.length; i++) {
        const tier = this.tiers[i];
        const circle = items[i] ? items[i].querySelector('.quantity-meter-addon-item__circle') : null;
        const circleInner = items[i] ? items[i].querySelector('.quantity-meter-addon-item__circle-inner') : null;
        const estAtteint = totalQuantity >= tier.qty;

        if (estAtteint) {
          if (circle) circle.classList.add('filled');
          if (circleInner) circleInner.classList.add('filled');
          lastActiveTier = i;
          currentTier = i;
        } else {
          if (circle) circle.classList.remove('filled');
          if (circleInner) circleInner.classList.remove('filled');
        }
      }

      for (let i = 0; i < this.tiers.length; i++) {
        const priceElement = items[i] ? items[i].querySelector('.quantity-meter-addon-item__price') : null;
        const estPrixActif = (i === lastActiveTier);

        if (estPrixActif) {
          if (priceElement) priceElement.classList.remove('subdued');
        } else {
          if (priceElement) priceElement.classList.add('subdued');
        }
      }

      if (items.length > 0) {
        const firstCircle = items[0].querySelector('.quantity-meter-addon-item__circle');
        const lastCircle = items[items.length - 1].querySelector('.quantity-meter-addon-item__circle');
        const activeCircle = items[currentTier].querySelector('.quantity-meter-addon-item__circle');

        if (firstCircle && lastCircle && activeCircle) {
          const CORRECTION = 10;

          const firstCenter = firstCircle.offsetLeft + firstCircle.offsetWidth / 2 + CORRECTION;
          const lastCenter = lastCircle.offsetLeft + lastCircle.offsetWidth / 2 + CORRECTION;
          const activeCenter = activeCircle.offsetLeft + activeCircle.offsetWidth / 2 + CORRECTION;

          const bgLine = this.quantityMeter.querySelector('.quantity-meter-addon-line');
          if (bgLine) {
            bgLine.style.left = firstCenter + 'px';
            bgLine.style.width = (lastCenter - firstCenter) + 'px';
          }

          if (progressLine) {
            if (currentTier > 0) {
              progressLine.style.left = firstCenter + 'px';
              progressLine.style.width = (activeCenter - firstCenter) + 'px';
            } else {
              progressLine.style.left = firstCenter + 'px';
              progressLine.style.width = '0';
            }
          }
        }
      }

      this.updateGifts(totalQuantity);
    }

    updateGifts(totalQuantity) {
      const event = new CustomEvent('bundlePackQuantityChanged', {
        detail: {
          totalQuantity: totalQuantity,
          selectedVariants: Array.from(this.selectedVariants.entries())
        }
      });
      document.dispatchEvent(event);
    }

    // ══════════════════════════════════════════
    // Price & Submit Button
    // ══════════════════════════════════════════

    updateButtonPrice() {
      if (!this.selectedVariants) return;

      let totalQuantity = 0;
      this.selectedVariants.forEach((data) => {
        totalQuantity += data.quantity;
      });

      let activeTier = null;
      for (let i = this.tiers.length - 1; i >= 0; i--) {
        if (totalQuantity >= this.tiers[i].qty) {
          activeTier = String(i + 1);
          break;
        }
      }

      let totalPriceBeforeDiscount = 0;
      this.selectedVariants.forEach((data) => {
        totalPriceBeforeDiscount += (data.price * data.quantity);
      });

      let totalCompareAtPrice = 0;
      this.selectedVariants.forEach((data) => {
        const comparePrice = data.compare_at_price || data.price;
        totalCompareAtPrice += (comparePrice * data.quantity);
      });

      let totalPrice = totalPriceBeforeDiscount;
      const discount = this.discounts[activeTier];

      if (discount && discount.type !== 'none') {
        if (discount.type === 'percentage') {
          const reductionMultiplier = (1 - discount.value / 100);
          let discountedTotal = 0;
          this.selectedVariants.forEach((data) => {
            const unitDiscounted = Math.ceil(data.price * reductionMultiplier);
            discountedTotal += unitDiscounted * data.quantity;
          });
          totalPrice = discountedTotal;
        } else if (discount.type === 'fixedPerUnit') {
          /* Montant fixe retiré à CHAQUE pot, supplément de saveur intact.
             Doit rester aligné sur "fixedAmountPerUnit" côté remise Shopify. */
          const discountInCentimes = discount.value * 100;
          let discountedTotal = 0;
          this.selectedVariants.forEach((data) => {
            const unitDiscounted = Math.max(0, data.price - discountInCentimes);
            discountedTotal += unitDiscounted * data.quantity;
          });
          totalPrice = discountedTotal;
        } else if (discount.type === 'fixed') {
          const discountInCentimes = discount.value * 100;
          totalPrice = totalPriceBeforeDiscount - discountInCentimes;
        }
      }

      totalPrice = Math.round(totalPrice);

      const event = new CustomEvent('bundlePackPrice:change', {
        detail: {
          totalPrice: totalPrice,
          totalPriceBeforeDiscount: totalPriceBeforeDiscount,
          totalCompareAtPrice: totalCompareAtPrice,
          selectedVariants: Array.from(this.selectedVariants.entries()),
          hasSelections: this.selectedVariants.size > 0,
          activeTier: activeTier,
          totalQuantity: totalQuantity,
          discount: discount,
          globalDiscountApplied: false
        }
      });
      document.dispatchEvent(event);
    }

    // Mémorise l'état rendu par Liquid AVANT toute réécriture : c'est le seul moyen de
    // restaurer un bouton correct (libellé traduit, état sold-out) plutôt que d'en inventer un.
    captureInitialSubmitState() {
      const submitButton = document.querySelector('.product-form__submit');
      if (!submitButton) return;
      const textEl = submitButton.querySelector('.product-form__submit-text');
      this.initialSubmitLabel = textEl ? textEl.textContent : null;
      this.initialSubmitDisabled = submitButton.disabled;
    }

    updateSubmitButtonState() {
      const submitButton = document.querySelector('.product-form__submit');
      if (!submitButton) return;

      const textEl = submitButton.querySelector('.product-form__submit-text');
      const separatorEl = submitButton.querySelector('.product-form__submit-separator');
      const priceEl = submitButton.querySelector('.product-form__submit-price');
      if (!textEl) return;

      const hasSelections = this.selectedVariants.size > 0;

      // Avec le bloc abonnement, une page sans sélection bundle reste un achat valide
      // (l'interception est neutre tant que selectedVariants est vide) : on ne doit pas
      // griser le bouton, sinon le client ne peut pas s'abonner au produit consulté.
      if (!hasSelections && document.querySelector('.subscribe-radio')) {
        submitButton.disabled = this.initialSubmitDisabled === true;
        submitButton.style.opacity = '';
        submitButton.style.cursor = '';
        if (this.initialSubmitLabel !== null && this.initialSubmitLabel !== undefined) {
          textEl.textContent = this.initialSubmitLabel;
        }
        if (separatorEl) separatorEl.style.display = '';
        if (priceEl) priceEl.style.display = '';
        return;
      }

      if (!hasSelections) {
        submitButton.disabled = true;
        submitButton.style.opacity = '0.5';
        submitButton.style.cursor = 'not-allowed';
        textEl.textContent = 'Sélectionnez un produit';
        if (separatorEl) separatorEl.style.display = 'none';
        if (priceEl) priceEl.style.display = 'none';
      } else {
        submitButton.disabled = false;
        submitButton.style.opacity = '';
        submitButton.style.cursor = '';
        textEl.textContent = 'Ajouter';
        if (separatorEl) separatorEl.style.display = '';
        if (priceEl) priceEl.style.display = '';
      }
    }

    // ══════════════════════════════════════════
    // Cart Interception
    // ══════════════════════════════════════════

    interceptFetchRequests() {
      const originalFetch = window.fetch;
      const self = this;

      window.fetch = function(...args) {
        const [url, options] = args;

        if (url && (url.includes('/cart/add') || url.includes('cart_add_url'))) {
          let isFromMainForm = false;

          if (options && options.body instanceof FormData) {
            const formData = options.body;
            const formSectionId = formData.get('section-id');
            const mainFormSectionId = self.productForm ? self.productForm.querySelector('input[name="section-id"]')?.value : null;

            if (mainFormSectionId && formSectionId === mainFormSectionId) {
              isFromMainForm = true;
            }
          }

          const shouldIntercept = isFromMainForm && self.selectedVariants.size > 0;

          if (shouldIntercept) {
            let sellingPlanId = null;
            let formProperties = {};
            if (options && options.body instanceof FormData) {
              sellingPlanId = options.body.get('selling_plan');
              for (const [key, value] of options.body.entries()) {
                const match = key.match(/^properties\[(.+)\]$/);
                if (match && value) {
                  formProperties[match[1]] = value;
                }
              }
            }

            const items = [];
            self.selectedVariants.forEach((data, variantId) => {
              const item = {
                id: parseInt(variantId),
                quantity: data.quantity
              };
              // Shopify rejette TOUT le lot si une seule ligne porte un plan que sa
              // variante n'a pas. On ne tamponne donc que les variantes éligibles.
              if (sellingPlanId && data.hasSellingPlan) {
                item.selling_plan = parseInt(sellingPlanId);
              }
              if (Object.keys(formProperties).length > 0) {
                item.properties = formProperties;
              }
              items.push(item);
            });

            // Ajouter le bocal upsell-gift si sélectionné
            if (window.__upsellGift) {
              const giftItem = window.__upsellGift.getSelectedItem();
              if (giftItem) {
                items.push({ id: giftItem.id, quantity: giftItem.quantity });
              }
            }

            const jsonBody = { items: items };

            if (options && options.body instanceof FormData) {
              const formData = options.body;
              const sections = formData.get('sections');
              const sections_url = formData.get('sections_url');
              if (sections) jsonBody.sections = sections;
              if (sections_url) jsonBody.sections_url = sections_url;
            }

            options.body = JSON.stringify(jsonBody);

            if (!options.headers) options.headers = {};
            if (options.headers instanceof Headers) {
              options.headers.set('Content-Type', 'application/json');
              options.headers.set('Accept', 'application/json');
            } else {
              options.headers['Content-Type'] = 'application/json';
              options.headers['Accept'] = 'application/json';
            }
          }

          const fetchPromise = originalFetch.apply(this, args);

          if (isFromMainForm && self.selectedVariants.size > 0) {
            return fetchPromise.then(response => {
              if (response.ok) {
                setTimeout(() => {
                  self.clearLocalStorage();
                  self.reset();
                }, 800);
              }
              return response;
            }).catch(error => {
              console.error('[BundlePackPresets] Erreur:', error);
              throw error;
            });
          }

          return fetchPromise;
        }

        return originalFetch.apply(this, args);
      };
    }

    updateCartUI(cartData) {
      fetch('/cart.js')
        .then(response => response.json())
        .then(cart => {
          const cartCount = document.querySelector('cart-notification-button .cart-count-bubble span, #cart-icon-bubble span');
          if (cartCount) {
            cartCount.textContent = cart.item_count;
          }

          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer && typeof cartDrawer.open === 'function') {
            cartDrawer.open();
          } else {
            const cartNotification = document.querySelector('cart-notification');
            if (cartNotification && typeof cartNotification.open === 'function') {
              cartNotification.open();
            }
          }
        })
        .catch(error => {
          // Silently fail
        });
    }

    // ══════════════════════════════════════════
    // localStorage
    // ══════════════════════════════════════════

    saveToLocalStorage() {
      const storageKey = 'bundle_presets_selections_' + window.location.pathname;
      const selections = {};

      this.selectedVariants.forEach((data, variantId) => {
        selections[variantId] = data;
      });

      try {
        localStorage.setItem(storageKey, JSON.stringify(selections));
      } catch (e) {
        console.error('Erreur sauvegarde localStorage:', e);
      }
    }

    clearLocalStorage() {
      const storageKey = 'bundle_presets_selections_' + window.location.pathname;
      try {
        localStorage.removeItem(storageKey);
      } catch (e) {
        console.error('Erreur suppression localStorage:', e);
      }
    }

    // ══════════════════════════════════════════
    // Grid Layout
    // ══════════════════════════════════════════

    initGridLayout() {
      this.isGridLayout = this.container.dataset.gridLayout === 'true';
      if (!this.isGridLayout) return;

      const grid = this.container.querySelector('.bundle-pack-grid');
      if (grid) grid.classList.add('grid-mode');

      const scrollWrapper = this.container.querySelector('.bundle-pack-scroll-wrapper');
      if (scrollWrapper) {
        scrollWrapper.style.maxHeight = 'none';
        scrollWrapper.style.overflow = 'visible';
        const indicator = scrollWrapper.querySelector('.bundle-pack-scroll-indicator');
        if (indicator) indicator.style.display = 'none';
      }
    }

    // ══════════════════════════════════════════
    // Scroll Indicator
    // ══════════════════════════════════════════

    initializeScrollIndicator() {
      const scrollWrapper = this.container.querySelector('.bundle-pack-scroll-wrapper');
      if (!scrollWrapper) return;

      const checkScrollNeeded = () => {
        const grid = scrollWrapper.querySelector('.bundle-pack-grid');
        const contentHeight = grid ? grid.scrollHeight : scrollWrapper.scrollHeight;
        const hasScroll = contentHeight > scrollWrapper.clientHeight + 10;
        if (!hasScroll) {
          scrollWrapper.classList.add('no-scroll');
        } else {
          scrollWrapper.classList.remove('no-scroll');
        }
      };

      const handleScroll = () => {
        const scrollTop = scrollWrapper.scrollTop;
        const scrollHeight = scrollWrapper.scrollHeight;
        const clientHeight = scrollWrapper.clientHeight;
        const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10;

        if (isAtBottom) {
          scrollWrapper.classList.add('scrolled-to-bottom');
        } else {
          scrollWrapper.classList.remove('scrolled-to-bottom');
        }
      };

      checkScrollNeeded();
      handleScroll();

      scrollWrapper.addEventListener('scroll', handleScroll);

      setTimeout(() => {
        checkScrollNeeded();
        handleScroll();
      }, 500);

      window.addEventListener('resize', () => {
        checkScrollNeeded();
        handleScroll();
      });
    }

    // ══════════════════════════════════════════
    // Reset
    // ══════════════════════════════════════════

    reset() {
      this.selectedVariants.clear();

      this.quantityInputs.forEach(input => {
        input.value = 0;
        this.updateQuantityButtons(input);
      });

      this.cards.forEach(card => {
        card.classList.remove('bundle-pack-card--selected');
      });

      // Revenir au pack libre
      const pills = this.container.querySelectorAll('.bundle-presets-pill');
      pills.forEach(pill => pill.classList.remove('bundle-presets-pill--active'));
      const freePill = this.container.querySelector('[data-preset-index="free"]');
      if (freePill) freePill.classList.add('bundle-presets-pill--active');
      this.activePresetIndex = 'free';

      this.updateQuantityMeter();
      this.updateButtonPrice();
      this.updateSubmitButtonState();
    }

    // ══════════════════════════════════════════
    // Public API
    // ══════════════════════════════════════════

    getSelectedVariants() {
      return Array.from(this.selectedVariants.entries());
    }

    getTotalQuantity() {
      let total = 0;
      this.selectedVariants.forEach(data => {
        total += data.quantity;
      });
      return total;
    }

    getTotalPrice() {
      let total = 0;
      this.selectedVariants.forEach(data => {
        total += (data.price * data.quantity);
      });
      return total;
    }
  }

  // Auto-initialize when DOM is ready
  function initializeBundlePackPresets() {
    const containers = document.querySelectorAll('[data-bundle-presets]');
    containers.forEach(container => {
      if (!container.bundlePackPresets) {
        container.bundlePackPresets = new BundlePackPresets(container);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeBundlePackPresets);
  } else {
    initializeBundlePackPresets();
  }

  document.addEventListener('shopify:section:load', initializeBundlePackPresets);

  window.BundlePackPresets = BundlePackPresets;

})();
